"use client"

import Link from "next/link"
import {
  Activity,
  Bot,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Gauge,
  LogOut,
  PanelTop,
  Wrench,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"

interface MaintenanceScreenProps {
  username?: string | null
  onLogout?: () => void
}

interface MaintenanceItem {
  title: string
  description: string
  status: string
  icon: LucideIcon
  tone: string
}

const maintenanceItems: MaintenanceItem[] = [
  {
    title: "Painel",
    description: "Melhorias finalizadas, com liberação gradual para manter a estabilidade.",
    status: "Em liberação",
    icon: PanelTop,
    tone: "bg-sky-500/10 text-sky-300 ring-sky-400/20",
  },
  {
    title: "Bots",
    description: "Ajustes de desempenho prontos, passando por validação em produção.",
    status: "Validando",
    icon: Bot,
    tone: "bg-emerald-500/10 text-emerald-300 ring-emerald-400/20",
  },
  {
    title: "Desempenho",
    description: "Monitoramento final de tempo de resposta, filas e uso de recursos.",
    status: "Monitorando",
    icon: Gauge,
    tone: "bg-amber-500/10 text-amber-300 ring-amber-400/20",
  },
]

const maintenanceSteps = [
  { label: "Preparação", status: "Concluída", done: true },
  { label: "Atualização", status: "Concluída", done: true },
  { label: "Liberação", status: "Em andamento", done: false },
]

export function MaintenanceScreen({ username, onLogout }: MaintenanceScreenProps) {
  return (
    <main className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col justify-center px-6 py-10">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center">
          <section className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1 text-xs font-medium text-zinc-300">
              <Wrench className="h-3.5 w-3.5" aria-hidden="true" />
              Manutenção programada
            </div>

            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-zinc-50 sm:text-4xl">
              Em manutenção
            </h1>

            <p className="mt-3 text-base leading-7 text-zinc-400">
              A atualização principal já foi concluída. Estamos fazendo uma liberação controlada em
              produção para validar o painel e o desempenho dos bots com segurança antes de reabrir
              os acessos.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4">
                <div className="flex items-center gap-3">
                  <CalendarClock className="h-5 w-5 text-zinc-400" aria-hidden="true" />
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Previsão</p>
                    <p className="mt-1 text-sm font-medium text-zinc-100">
                      00:00 BRT, 24 de maio de 2026
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-zinc-400" aria-hidden="true" />
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Status</p>
                    <p className="mt-1 text-sm font-medium text-zinc-100">
                      Liberação controlada
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                href="/auth/login"
                className="inline-flex h-10 items-center justify-center rounded-lg bg-zinc-100 px-4 text-sm font-medium text-zinc-950 transition-colors hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-zinc-100/40"
              >
                Entrar no painel
              </Link>

              {username && onLogout ? (
                <button
                  type="button"
                  onClick={onLogout}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-zinc-800 px-4 text-sm font-medium text-zinc-300 transition-colors hover:bg-zinc-900 hover:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-100/20"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sair de {username}
                </button>
              ) : null}
            </div>
          </section>

          <aside className="space-y-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-zinc-100">Status da atualização</p>
                <p className="mt-1 text-sm text-zinc-500">Validação final em produção</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-200">
                <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
                Janela ativa
              </div>
            </div>

            <div
              role="progressbar"
              aria-label="Progresso da manutenção"
              aria-valuenow={85}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-2 overflow-hidden rounded-full bg-zinc-800"
            >
              <div className="h-full w-5/6 rounded-full bg-zinc-200" />
            </div>

            <div className="space-y-3">
              {maintenanceItems.map((item) => {
                const Icon = item.icon

                return (
                  <div key={item.title} className="rounded-xl border border-zinc-800 bg-zinc-900/75 p-4">
                    <div className="flex gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ${item.tone}`}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-100">{item.title}</p>
                          <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs font-medium text-zinc-300">
                            {item.status}
                          </span>
                        </div>
                        <p className="mt-1 text-sm leading-6 text-zinc-500">{item.description}</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
              {maintenanceSteps.map((step) => (
                <div key={step.label} className="flex items-center gap-3 rounded-lg bg-zinc-950/35 px-3 py-2">
                  {step.done ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-300" aria-hidden="true" />
                  ) : (
                    <Clock3 className="h-4 w-4 text-zinc-500" aria-hidden="true" />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-zinc-200">{step.label}</p>
                    <p className="text-xs text-zinc-500">{step.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </main>
  )
}
